import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// components
import SetPassword from "../../components/SetPassword";
import FormButton from "../../formComponents/FormButton";
import InlineInput from "../../formComponents/InlineInput";
import ErrorDisplay from "../../components/ErrorDisplay";
import Loading from "../../components/Loading";

// styles and images
import "./AccountSettings.css";
import "../project/Project.css";

// store
import { messageAndErrorStore, userStore } from "../../store/Store";

// hooks
import useGetFetch from "../../hooks/useGetFetch";

export default function AccountSettings() {
  // global state
  const currentUser = userStore((data) => data.currentUser);
  const JwtToken = userStore((data) => data.JwtToken);
  const setAuthStatus = userStore((data) => data.setAuthStatus);
  const toggleNewMessage = messageAndErrorStore(data => data.toggleNewMessage)

  // state
  const [pending, setPending] = useState(false);
  const [passwordValid, setPasswordValid] = useState(false);
  const [clearPassword, setClearPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [fileError, setFileError] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [user, setUser] = useState({
    id: 0,
    display_name: "",
    photo_url: "",
    first_name: "",
    last_name: "",
    company_id: 0,
    updated_by_id: 0,
    user_password: "",
  });
  // get user data
  const navigate = useNavigate();
  const {
    theData,
    isPending,
    error: retrievalError,
    statusCode,
  } = useGetFetch(`${process.env.REACT_APP_BACKEND}/lc/user/${currentUser.id}`);

  useEffect(() => {
    if (theData) {
      setUser((current) => {
        return {
          ...current,
          id: theData.id,
          first_name: theData.first_name,
          last_name: theData.last_name,
          company_id: theData.company_id,
          display_name: theData.display_name,
          photo_url: theData.photo_url,
          updated_by_id: 0,
        };
      });
    }
  }, [theData]);

  const handleChange = (e) => {
    setUser({
      ...user,
      [e.target.name]: e.target.value,
    });
  };

  const handlePhoto = (e) => {
    let thumb = e.target.files[0];
    setFileError(null);
    if (!thumb) {
      setFileError("Please select a file");
      return;
    } else if (!thumb.type.includes("image")) {
      setFileError("File must be an image");
      return;
    } else if (thumb.size > 1000000) {
      setFileError("File must be smaller than 1 MB");
      return;
    }
    setPhoto(thumb);
  };
  const controller = new AbortController();
  const headers = new Headers();
  headers.append("Content-Type", "application/json");
  headers.append("Authorization", "Bearer " + JwtToken);
  const requestOptions = {
    method: "PATCH",
    headers,
    signal: controller.signal,
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setPending(true);
    const body = user;
    body.updated_by_id = currentUser.id;
    body.photo_url = `${user.id}-${user.display_name}`;
    requestOptions.body = JSON.stringify(body);
    fetch(`${process.env.REACT_APP_BACKEND}/lc/user/${user.id}`, requestOptions)
      .then((res) => {
        if (!res.ok) {
          if (res.status === 401) {
            setAuthStatus(false);
            navigate("/login");
            return;
          }
          setPending(false);
          toggleNewMessage("Could not update your profile");
          return;
        }
        setPending(false);
        navigate(0);
        return res.json();
      })
      .then((data) => {
        if (photo) {
          const formData = new FormData();
          formData.append("file", photo);
          formData.append("file_id", user.id);
          formData.append("file_path", "profile-pics");
          const photoHeaders = new Headers();
          photoHeaders.append("Authorization", "Bearer " + JwtToken);
          const photoRequestOptions = {
            method: "POST",
            body: formData,
            headers: photoHeaders,
          };
          fetch(
            `${process.env.REACT_APP_BACKEND}/lc/upload-file`,
            photoRequestOptions
          )
            .then((res) => res.json())
            .then((_) => {
              toggleNewMessage(null, "Profile updated");
              setPending(false);
              navigate(0);
            });
        }
      })
      .catch((_) => {
        toggleNewMessage("Could not update your profile");
        return;
      })
      .finally(() => setPending(false));
    return () => controller.abort();
  };

  const handlePassword = (e) => {
    e.preventDefault();
    setPending(true);
    if (!passwordValid) {
      toggleNewMessage("Password is invalid");
      setPending(false);
      return;
    }
    const body = user;
    body.user_password = password;
    body.updated_by_id = currentUser.id;
    requestOptions.body = JSON.stringify(body);
    fetch(`${process.env.REACT_APP_BACKEND}/lc/user/${user.id}`, requestOptions)
      .then((res) => {
        if (!res.ok) {
          if (res.status === 401) {
            setAuthStatus(false);
            navigate("/login");
            return;
          }
          toggleNewMessage(`Something went wrong Status ${res.status}`);
          return;
        }
        setPending(false);
        return res.json();
      })
      .then((_) => {
        setPending(false);
        toggleNewMessage(null, "Password Updated");
      })
      .catch((_) => {
        toggleNewMessage("Could not update your password");
        setPending(false);
        return;
      })
      .finally(() => setPending(false));
    setClearPassword(true);
    navigate(0);
    return () => controller.abort();
  };
  if (retrievalError) {
    return <ErrorDisplay message={retrievalError} code={statusCode} />;
  } else {
    return !isPending ? (
      <div className="container-fluid">
        <div className="d-flex justify-content-between project-header border-bottom">
          <h2>Account Settings</h2>
        </div>
        {!currentUser && (
          <p className="error">Could not retreive your profile</p>
        )}
        {currentUser && (
          <div className="user-profile">
            <form onSubmit={handleSubmit}>
              <InlineInput
                name={"first_name"}
                title={"First Name"}
                className={"form-control"}
                divClass={"mb-3"}
                type={"text"}
                placeholder={"First Name"}
                onChange={handleChange}
                value={user.first_name}
              />
              <InlineInput
                name={"last_name"}
                title={"Last Name"}
                className={"form-control"}
                divClass={"mb-3"}
                type={"text"}
                placeholder={"Last Name"}
                onChange={handleChange}
                value={user.last_name}
              />
              <InlineInput
                name={"display_name"}
                title={"Display Name"}
                className={"form-control"}
                divClass={"mb-3"}
                type={"text"}
                placeholder={"Display Name"}
                onChange={handleChange}
                value={user.display_name}
              />
              <InlineInput
                name={"photo_url"}
                title={"Upload Photo"}
                className={"form-control"}
                divClass={"mb-3"}
                type={"file"}
                placeholder={"Upload Photo"}
                onChange={handlePhoto}
              />
              {fileError && <p className="error">{fileError}</p>}
              <div className="row mb-3">
                <div className="col-auto">
                  <FormButton pending={pending} name={"Update Profile"} />
                </div>
              </div>
            </form>
            <div className="row mb-3">
              <div className="col-sm-5 ">
                <form onSubmit={handlePassword}>
                  <SetPassword
                    setPassword={setPassword}
                    setPasswordValid={setPasswordValid}
                    clearPassword={clearPassword}
                  />
                  <FormButton pending={pending} name={"Update Password"} />
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    ) : (
      <Loading />
    );
  }
}
